import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';
import Logo from './Logo';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);

  const isActive = (path: string) => location.pathname === path;

  // Detect scroll for background effect adjustment if needed in future, 
  // currently keeping backdrop-blur consistent.
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Servicios', path: '/servicios' },
    { name: 'Proyectos', path: '/proyectos' },
    { name: 'Sobre Nosotros', path: '/sobre-nosotros' },
  ];

  return (
    <nav className={`fixed w-full z-50 top-0 start-0 border-b transition-all duration-300 ${scrolled ? 'bg-mira-bg/95 border-mira-border/80 shadow-lg backdrop-blur-xl' : 'bg-mira-bg/80 border-mira-border/30 backdrop-blur-md'}`}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10">
        <div className="flex flex-wrap items-center justify-between py-5">
          
          {/* Logo & Brand */}
          <Link to="/" className="flex items-center gap-4 group">
            <div className="bg-mira-accent/5 p-2.5 rounded-xl border border-mira-accent/20 group-hover:border-mira-accent/50 group-hover:shadow-[0_0_15px_rgba(26,140,255,0.3)] transition-all duration-300">
              <Logo className="w-7 h-7 text-mira-accent" />
            </div>
            <span className="self-center text-2xl font-semibold whitespace-nowrap text-white tracking-tight group-hover:text-gray-100 transition-colors">
              Mira<span className="text-mira-accent">-IA</span>
            </span>
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            type="button"
            className="inline-flex items-center p-2 w-10 h-10 justify-center text-sm text-gray-400 rounded-lg md:hidden hover:bg-mira-surface focus:outline-none focus:ring-2 focus:ring-mira-accent transition-colors"
            aria-controls="navbar-default"
            aria-expanded={isOpen}
          >
            <span className="sr-only">Open main menu</span>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Desktop Menu */}
          <div className="hidden w-full md:block md:w-auto" id="navbar-default">
            <ul className="flex flex-col p-4 md:p-0 mt-4 md:flex-row md:items-center md:space-x-10 md:mt-0 md:bg-transparent">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`relative py-2 text-[15px] font-medium tracking-wide transition-colors duration-300 group ${
                      isActive(link.path)
                        ? 'text-white'
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {link.name}
                    {/* Elegant underline hover effect */}
                    <span className={`absolute bottom-0 left-0 w-full h-[1px] bg-mira-accent transform transition-transform duration-300 origin-left ${
                        isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}></span>
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/contacto"
                  className="flex items-center gap-2 py-2.5 px-6 text-xs font-bold uppercase tracking-widest text-mira-accent border border-mira-accent/50 rounded-full hover:bg-mira-accent hover:text-white hover:border-mira-accent transition-all duration-300 hover:shadow-[0_0_15px_rgba(26,140,255,0.4)]"
                >
                  Contacto <ChevronRight className="w-3 h-3" />
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isOpen && (
        <div className="md:hidden bg-mira-surface border-b border-mira-border animate-fade-in shadow-2xl">
          <ul className="flex flex-col p-6 space-y-3">
            {navLinks.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`block py-3 px-4 rounded-lg transition-all text-base font-medium ${
                    isActive(link.path)
                      ? 'bg-mira-accent/10 text-mira-accent border border-mira-accent/30'
                      : 'text-gray-300 hover:bg-mira-bg hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/contacto"
                onClick={() => setIsOpen(false)}
                className="block text-center py-3 px-4 rounded-lg border border-mira-accent text-mira-accent font-bold mt-6 hover:bg-mira-accent hover:text-white transition-all uppercase tracking-widest text-sm"
              >
                Empezar Ahora
              </Link>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
};

export default Navbar;